import React from "react";
import {
  Box, Container, Heading, Text, VStack, SimpleGrid,
  HStack, Button, Icon
} from "@chakra-ui/react";
import { FaFlask, FaTint, FaArrowRight, FaCheckCircle, FaShieldAlt } from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";

const NAVY = "#141C49";
const WATER_BLUE = "#6FAFD6";
const DEEP_AQUA = "#2C7DA0";
const ICE_MIST = "#EAF3FB";

const WaterPurifiersUnderSink: React.FC = () => {
  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/water/water-purifiers/under-sink-system"
        fallbackTitle="Under Sink Water Purifier — One Water Australia"
        fallbackDescription="Under-sink water purifiers professionally installed for Australian homes. Multi-stage purification system specified for your water."
      />
      <NavbarWithCallToAction />

      {/* Hero */}
      <Box position="relative" bg={NAVY} color="white" py={{ base: 16, md: 24 }} overflow="hidden">
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgImage="url('https://images.unsplash.com/photo-1621905251918-48416bd8575a?auto=format&fit=crop&w=1920&q=80')" bgSize="cover" bgPosition="center" zIndex={0} />
        <Box position="absolute" top={0} right={0} bottom={0} left={0} bgGradient={`linear(to-br, ${NAVY}E8, ${DEEP_AQUA}AA)`} zIndex={0} />
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center" maxW="3xl" mx="auto">
            <Icon as={FaFlask} boxSize={12} color={WATER_BLUE} />
            <Heading size="2xl" fontWeight="bold" letterSpacing="-0.02em">
              Purified drinking water from your kitchen tap.
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.800">
              Professionally installed. Designed for Australian water. Maintained by the same team.
            </Text>
            <Button
              as={RouterLink}
              to="/contact"
              bg={WATER_BLUE}
              color={NAVY}
              size="lg"
              fontWeight="bold"
              rightIcon={<FaArrowRight />}
              _hover={{ bg: "#8DC5E0" }}
              transition="all 0.2s"
            >
              Book a Free Water Assessment
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* The problem with half-measures */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={6}>
            <Heading size="xl" color={NAVY}>The Problem With Half-Measures</Heading>
            <Text color="gray.600" fontSize="lg">
              If you've been using a jug filter, you've been doing the right thing. A dedicated under-sink purifier system simply does it better — comprehensively, consistently, and without the daily routine.
            </Text>
            <Text color="gray.600">
              A jug filters the water you remember to filter. An under-sink purifier filters every drop from that tap, automatically, without thinking about it. And it does it more thoroughly.
            </Text>
            <Box borderLeft={`3px solid ${WATER_BLUE}`} pl={4}>
              <Text color="gray.600" fontStyle="italic" fontSize="sm">
                "You've been doing the right thing. A dedicated system simply does it better."
              </Text>
              <Text fontSize="xs" color="gray.500" mt={1}>— We don't trash other products. We respect your existing habits.</Text>
            </Box>
          </VStack>
          <VStack spacing={5}>
            <Box borderRadius="xl" overflow="hidden" h="220px" w="full" shadow="md">
              <Box
                as="img"
                src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?auto=format&fit=crop&w=800&q=80"
                alt="Under-sink water purifier system with multi-stage filtration"
                w="full"
                h="full"
                objectFit="cover"
              />
            </Box>
            <Box p={8} bg={ICE_MIST} borderRadius="2xl" w="full">
              <Heading size="md" color={NAVY} mb={6}>Multi-Stage Purification</Heading>
              <VStack spacing={4} align="stretch">
                {[
                  { n: "1", name: "Sediment Pre-Filter", desc: "Removes particles, rust, and suspended solids — protects the stages that follow." },
                  { n: "2", name: "Activated Carbon", desc: "Removes chlorine, chloramines, taste, odour, and VOCs." },
                  { n: "3", name: "RO Membrane or Sub-Micron", desc: "System-dependent — addresses dissolved solids, heavy metals, or fine contaminants." },
                  { n: "4", name: "Post-Polish Filter", desc: "Final refinement. What reaches your glass is the cleanest possible water." },
                ].map((s) => (
                  <HStack key={s.n} spacing={4} align="flex-start">
                    <Box
                      w={8} h={8}
                      borderRadius="full"
                      bg={DEEP_AQUA}
                      color="white"
                      display="flex" alignItems="center" justifyContent="center"
                      fontSize="sm" fontWeight="bold" flexShrink={0}
                    >
                      {s.n}
                    </Box>
                    <Box>
                      <Text fontWeight="600" color={NAVY} fontSize="sm">{s.name}</Text>
                      <Text fontSize="xs" color="gray.600">{s.desc}</Text>
                    </Box>
                  </HStack>
                ))}
              </VStack>
            </Box>
          </VStack>
        </SimpleGrid>
      </Container>

      {/* Benefits */}
      <Box bg="gray.50" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={4} mb={12} textAlign="center">
            <Heading size="xl" color={NAVY}>What Changes</Heading>
          </VStack>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {[
              { icon: FaTint, title: "Noticeably Better Water", desc: "Taste and clarity difference is immediate. No chlorine, no metallic aftertaste." },
              { icon: FaFlask, title: "Better Coffee & Cooking", desc: "Water quality directly affects everything you cook or brew with it." },
              { icon: FaShieldAlt, title: "Ice That Tastes Like Ice", desc: "Purified water makes clean, clear, tasteless ice cubes." },
              { icon: FaCheckCircle, title: "Zero Bottled Water", desc: "Permanently. Better purity, no plastic, no cost, no carrying." },
            ].map((b) => (
              <Box key={b.title} p={6} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.200" textAlign="center">
                <Icon as={b.icon} boxSize={8} color={WATER_BLUE} mb={4} />
                <Heading size="sm" color={NAVY} mb={2}>{b.title}</Heading>
                <Text fontSize="sm" color="gray.600">{b.desc}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Why One Water */}
      <Container maxW="container.xl" py={20}>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={16} alignItems="center">
          <VStack align="flex-start" spacing={5}>
            <Heading size="xl" color={NAVY}>Why One Water Australia</Heading>
            {[
              { title: "Professional specification", desc: "We assess your water supply before selecting the right purification stages." },
              { title: "UNSW lab testing partnership", desc: "Independent, accredited testing — not manufacturer claims or self-reporting." },
              { title: "Ongoing support included", desc: "The same team that installs your system services and maintains it long-term." },
              { title: "Part of One Group", desc: "Your water health connects to your energy efficiency, financial structure, and property growth." },
            ].map((item) => (
              <HStack key={item.title} spacing={4} align="flex-start">
                <Icon as={FaCheckCircle} color={DEEP_AQUA} boxSize={5} mt={0.5} flexShrink={0} />
                <Box>
                  <Text fontWeight="600" color={NAVY} mb={1}>{item.title}</Text>
                  <Text fontSize="sm" color="gray.600">{item.desc}</Text>
                </Box>
              </HStack>
            ))}
          </VStack>
          <Box p={10} bg={ICE_MIST} borderRadius="2xl">
            <VStack spacing={6} textAlign="center">
              <Heading size="lg" color={NAVY}>Book a free water assessment.</Heading>
              <Text color="gray.600" fontSize="sm">
                We assess your supply, explain our findings, and recommend the appropriate system. Sometimes that's an under-sink purifier. Sometimes it's something simpler. We'll tell you honestly.
              </Text>
              <Button
                as={RouterLink}
                to="/contact"
                bg={DEEP_AQUA}
                color="white"
                size="lg"
                w="full"
                rightIcon={<FaArrowRight />}
                _hover={{ bg: NAVY }}
                transition="all 0.2s"
              >
                Book Free Assessment
              </Button>
            </VStack>
          </Box>
        </SimpleGrid>
      </Container>

      {/* Social proof */}
      <Box bg={ICE_MIST} py={16}>
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {[
              { quote: "We had a jug filter for years. The purifier is completely different — every glass of water is noticeably better.", name: "Andrew L., Brisbane" },
              { quote: "The assessment gave us real data about what was actually in our water. That made the recommendation easy to trust.", name: "Kate S., Gold Coast" },
              { quote: "One team from start to finish. No handoffs to third-party installers or maintenance contractors.", name: "Michael B., Sunshine Coast" },
            ].map((t) => (
              <Box key={t.name} p={6} bg="white" borderRadius="xl" shadow="sm">
                <Text color="gray.600" fontStyle="italic" mb={4} fontSize="sm">"{t.quote}"</Text>
                <Text fontWeight="600" color={NAVY} fontSize="sm">— {t.name}</Text>
              </Box>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      <FooterWithFourColumns />
    </Box>
  );
};

export default WaterPurifiersUnderSink;
